import React, { useEffect, useState, useCallback, useRef } from "react";
import { Spinner, TextInput, Button } from "flowbite-react";
import { HiSearch, HiEye, HiX, HiRefresh, HiFilter, HiSortAscending, HiSortDescending, HiChevronDown } from "react-icons/hi";
import axios from "../api/axios";
import { useAuth } from "../../src/context/AuthContext";
import ApiUsageModal from "../modals/ApiUsageModal";

const PAGE_SIZE = 10;

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showApiModal, setShowApiModal] = useState(false);
  const [selectedUserApis, setSelectedUserApis] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('none'); // 'none', 'requests', 'successRate', 'responseTime', 'lastActivity'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [filterSuccessRate, setFilterSuccessRate] = useState('all'); // 'all', 'high', 'medium', 'low'
  const filterRef = useRef(null);

  const { getToken } = useAuth();

  // Debounce function to avoid too many API calls while typing
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const fetchAnalytics = async (pageNum = 1, search = "") => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      
      // Build query parameters
      const params = new URLSearchParams({
        page: pageNum.toString(),
        pageSize: PAGE_SIZE.toString()
      });
      
      // Add email search parameter if provided
      if (search.trim()) {
        params.append('email', search.trim());
      }
      
      const res = await axios.get(
        `https://api.foodoscope.com/admin/admin-metrics?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Handle the actual API response structure
      if (res.data && res.data.data) {
        setAnalytics(res.data.data);
        setTotalCount(res.data.totalCount || 0);
      } else {
        setAnalytics([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('API Error:', err);
      setError("Failed to fetch analytics data.");
    }
    setLoading(false);
    setSearchLoading(false);
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchTerm) => {
      setPage(1); // Reset to first page when searching
      fetchAnalytics(1, searchTerm);
    }, 500),
    []
  );

  // Handle search input changes
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSearchLoading(true);
    
    if (value.trim() === "") {
      // If search is cleared, fetch all data
      setPage(1);
      fetchAnalytics(1, "");
    } else {
      // Trigger debounced search
      debouncedSearch(value);
    }
  };

  // Initial load and page changes
  useEffect(() => {
    fetchAnalytics(page, searchQuery);
  }, [page]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if any filter is active
  const isFilterActive = sortBy !== 'none' || filterSuccessRate !== 'all';

  // Reset all filters
  const resetFilters = () => {
    setSortBy('none');
    setSortOrder('desc');
    setFilterSuccessRate('all');
  };

  // Get filtered and sorted analytics data
  const getFilteredAndSortedAnalytics = () => {
    let data = [...analytics];

    // Apply success rate filter
    if (filterSuccessRate !== 'all') {
      data = data.filter((user) => {
        const totalReqs = user.totalRequests || getTotalRequestsFromDaily(user.dailyUsage);
        const failedReqs = user.failedRequests || getTotalErrorsFromDaily(user.dailyUsage);
        const successfulReqs = user.successfulRequests || (totalReqs - failedReqs);
        const successRate = totalReqs > 0 ? (successfulReqs / totalReqs) * 100 : 0;

        switch (filterSuccessRate) {
          case 'high': return successRate >= 95;
          case 'medium': return successRate >= 80 && successRate < 95;
          case 'low': return successRate < 80;
          default: return true;
        }
      });
    }

    // Apply sorting
    if (sortBy !== 'none') {
      data.sort((a, b) => {
        let valA, valB;

        switch (sortBy) {
          case 'requests':
            valA = a.totalRequests || getTotalRequestsFromDaily(a.dailyUsage);
            valB = b.totalRequests || getTotalRequestsFromDaily(b.dailyUsage);
            break;
          case 'successRate': {
            const totalA = a.totalRequests || getTotalRequestsFromDaily(a.dailyUsage);
            const failedA = a.failedRequests || getTotalErrorsFromDaily(a.dailyUsage);
            const successA = a.successfulRequests || (totalA - failedA);
            valA = totalA > 0 ? (successA / totalA) * 100 : 0;

            const totalB = b.totalRequests || getTotalRequestsFromDaily(b.dailyUsage);
            const failedB = b.failedRequests || getTotalErrorsFromDaily(b.dailyUsage);
            const successB = b.successfulRequests || (totalB - failedB);
            valB = totalB > 0 ? (successB / totalB) * 100 : 0;
            break;
          }
          case 'responseTime':
            valA = a.averageResponseTime || a.proxyLatency?.api?.avg || 0;
            valB = b.averageResponseTime || b.proxyLatency?.api?.avg || 0;
            break;
          case 'lastActivity':
            valA = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
            valB = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
            break;
          default:
            return 0;
        }

        return sortOrder === 'asc' ? valA - valB : valB - valA;
      });
    }

    return data;
  };

  const openApiModal = (user) => {
    setSelectedUserApis({
      email: user.email,
      endpoints: user.endpointCounts || {}
    });
    setShowApiModal(true);
  };

  const closeApiModal = () => {
    setShowApiModal(false);
    setSelectedUserApis(null);
  };

  

  const getStatusColor = (successRate) => {
    if (successRate >= 95) return "text-green-600";
    if (successRate >= 80) return "text-yellow-600";
    return "text-red-500";
  };

  const getBarColor = (successRate) => {
    if (successRate >= 95) return "bg-green-500";
    if (successRate >= 80) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getTotalRequestsFromDaily = (dailyUsage) => {
    if (!dailyUsage) return 0;
    return Object.values(dailyUsage).reduce((total, day) => total + (day.requests || 0), 0);
  };

  const getTotalErrorsFromDaily = (dailyUsage) => {
    if (!dailyUsage) return 0;
    return Object.values(dailyUsage).reduce((total, day) => total + (day.errors || 0), 0);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, 2, 3, 4, 5);
      if (totalPages > 6) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="h-full bg-[#232323] rounded-2xl p-4 sm:p-6 lg:p-8 flex flex-col border border-[#2A2A2A] shadow-lg">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">User Analytics</h2>
          <p className="text-gray-400 text-sm">Monitor user activity and API usage metrics</p>
        </div>

        {/* Header Actions */}
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
          <button
            onClick={() => fetchAnalytics(page, searchQuery)}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-xl transition-colors shrink-0"
            title="Refresh"
          >
            <HiRefresh className="w-5 h-5" />
          </button>
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className={`p-2 rounded-xl transition-colors shrink-0 ${
                isFilterActive
                  ? 'text-blue-400 bg-blue-500/20 hover:bg-blue-500/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
              title="Filter"
            >
              <HiFilter className="w-5 h-5" />
            </button>

            {/* Filter Dropdown */}
            {showFilterDropdown && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-[#2A2B2D] border border-gray-700/50 rounded-xl shadow-2xl z-50 overflow-hidden">
                {/* Dropdown Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700/50">
                  <span className="text-sm font-bold text-white">Filters & Sort</span>
                  {isFilterActive && (
                    <button
                      onClick={resetFilters}
                      className="text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors"
                    >
                      Reset All
                    </button>
                  )}
                </div>

                {/* Sort By Section */}
                <div className="px-4 py-3 border-b border-gray-700/30">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Sort By</p>
                  <div className="space-y-1">
                    {[
                      { value: 'none', label: 'Default' },
                      { value: 'requests', label: 'Total Requests' },
                      { value: 'successRate', label: 'Success Rate' },
                      { value: 'responseTime', label: 'Avg Response Time' },
                      { value: 'lastActivity', label: 'Last Activity' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          if (sortBy === option.value && option.value !== 'none') {
                            // Toggle sort order if clicking the same option
                            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                          } else {
                            setSortBy(option.value);
                            setSortOrder('desc');
                          }
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                          sortBy === option.value
                            ? 'bg-blue-500/20 text-blue-400 font-semibold'
                            : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                        }`}
                      >
                        <span>{option.label}</span>
                        {sortBy === option.value && option.value !== 'none' && (
                          sortOrder === 'desc'
                            ? <HiSortDescending className="w-4 h-4" />
                            : <HiSortAscending className="w-4 h-4" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Filter by Success Rate */}
                <div className="px-4 py-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Success Rate</p>
                  <div className="space-y-1">
                    {[
                      { value: 'all', label: 'All Users', color: '' },
                      { value: 'high', label: '≥ 95% (Healthy)', color: 'text-green-400' },
                      { value: 'medium', label: '80% – 95% (Warning)', color: 'text-yellow-400' },
                      { value: 'low', label: '< 80% (Critical)', color: 'text-red-400' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setFilterSuccessRate(option.value)}
                        className={`w-full flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                          filterSuccessRate === option.value
                            ? 'bg-blue-500/20 text-blue-400 font-semibold'
                            : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                        }`}
                      >
                        {option.color && (
                          <span className={`w-2 h-2 rounded-full mr-2 ${option.color.replace('text-', 'bg-')}`}></span>
                        )}
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by user email..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-8 py-2 text-sm bg-[#2A2B2D] border border-gray-700/50 rounded-xl text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setPage(1);
                  fetchAnalytics(1, "");
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <HiX className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col justify-center items-center h-64 space-y-4">
          <Spinner size="xl" color="info" />
          <p className="text-gray-400">Loading analytics data...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
            <span className="text-red-400 text-2xl">!</span>
          </div>
          <div className="text-red-400 text-center">
            <p className="font-semibold">{error}</p>
            <p className="text-sm text-gray-500">Please try again later</p>
          </div>
          <Button color="gray" size="sm" onClick={() => fetchAnalytics(page, searchQuery)}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          {/* Search Results Info */}
          {searchQuery && (
            <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-blue-400 text-sm">
                <span className="font-semibold">Search Results:</span> Found {totalCount} users matching "{searchQuery}"
                {totalCount > PAGE_SIZE && ` (showing ${analytics.length} of ${totalCount})`}
              </p>
            </div>
          )}

          {/* Table Container - Cream/light themed */}
          <div className="rounded-xl overflow-hidden border border-[#c5c2b8] bg-[#F4F6F0]" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.10)' }}>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-[#B8B5AA]">
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-[#4a4a4a] uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-[#8C9086] uppercase tracking-wider">
                      Requests
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-[#8C9086] uppercase tracking-wider">
                      Success Rate
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-[#8C9086] uppercase tracking-wider">
                      Avg Response
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-[#8C9086] uppercase tracking-wider">
                      Endpoints
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-[#8C9086] uppercase tracking-wider">
                      Last Activity
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-black/[0.04]">
                  {getFilteredAndSortedAnalytics().length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-20 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4">
                          <div className="w-16 h-16 bg-[#D5DABF]/30 rounded-full flex items-center justify-center text-[#8C9086]">
                            <HiSearch className="w-8 h-8" />
                          </div>
                          <p className="text-[#8C9086] font-medium text-sm">
                            {searchQuery || isFilterActive ? "No users found matching criteria" : "No analytics data available"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    getFilteredAndSortedAnalytics().map((user, index) => {
                      const totalReqs = user.totalRequests || getTotalRequestsFromDaily(user.dailyUsage);
                      const failedReqs = user.failedRequests || getTotalErrorsFromDaily(user.dailyUsage);
                      const successfulReqs = user.successfulRequests || (totalReqs - failedReqs);
                      const successRate = totalReqs > 0 ? (successfulReqs / totalReqs) * 100 : 0;

                      return (
                        <tr
                          key={user._id}
                          className="hover:bg-[#DEE3D0] transition-colors duration-200 group"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-[#D5DABF] rounded-full flex items-center justify-center text-[#5C6353] font-bold text-xs">
                                {user.email.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-semibold text-[#161616] truncate max-w-[200px]">
                                {user.email}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center space-x-3 text-sm">
                              <span className="text-[#161616] font-bold">
                                {totalReqs.toLocaleString()}
                              </span>
                              <div className="flex items-center space-x-2 text-xs font-semibold">
                                <span className="text-green-600 flex items-center bg-green-500/10 px-1.5 py-0.5 rounded">
                                  {successfulReqs.toLocaleString()}
                                </span>
                                <span className="text-red-500 flex items-center bg-red-500/10 px-1.5 py-0.5 rounded">
                                  {failedReqs.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">
                            <div className="flex flex-col items-center space-y-1.5">
                              <span className="text-[#161616] font-bold text-sm">
                                {successRate.toFixed(1)}%
                              </span>
                              <div className="w-16 bg-[#D5DABF] rounded-full h-1.5 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-300 ${getBarColor(successRate)}`}
                                  style={{ width: `${successRate}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">
                            <span className="text-[#3A3A3A] font-bold text-sm">
                              {user.averageResponseTime?.toFixed(0) || user.proxyLatency?.api?.avg?.toFixed(0) || '-'}
                              <span className="text-[#8C9086] text-xs font-medium ml-1">ms</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">
                            <button
                              onClick={() => openApiModal(user)}
                              disabled={!user.endpointCounts || Object.keys(user.endpointCounts).length === 0}
                              className={`inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold transition-all duration-200 ${
                                user.endpointCounts && Object.keys(user.endpointCounts).length > 0
                                  ? 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20'
                                  : 'bg-black/5 text-[#8C9086] cursor-not-allowed'
                              }`}
                              title={!user.endpointCounts || Object.keys(user.endpointCounts).length === 0 ? "No APIs available" : "View all APIs"}
                            >
                              <HiEye className="w-3.5 h-3.5 mr-1.5" />
                              View
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">
                            <div className="flex flex-col justify-center items-center">
                              {user.lastUpdated ? (
                                <>
                                  <span className="text-sm font-semibold text-[#3A3A3A]">{new Date(user.lastUpdated).toLocaleDateString()}</span>
                                  <span className="text-xs font-medium text-[#8C9086]">{new Date(user.lastUpdated).toLocaleTimeString()}</span>
                                </>
                              ) : (
                                <span className="text-sm font-medium text-[#8C9086]">-</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */
            totalPages > 1 && (
              <div className="px-6 py-4 border-t border-[#D5DABF] bg-[#E8ECDA] flex items-center justify-center sm:justify-between flex-wrap gap-4 mt-auto rounded-b-3xl">
                <span className="text-sm text-[#8C9086] font-medium hidden sm:block">
                  Page <span className="text-[#3A3A3A] font-bold">{page}</span> of <span className="text-[#3A3A3A] font-bold">{totalPages}</span>
                </span>
                
                <div className="flex space-x-1">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 text-sm font-semibold text-[#8C9086] rounded-xl hover:bg-[#D5DABF] disabled:opacity-50 disabled:hover:bg-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#8C9086]/50"
                  >
                    &larr; Previous
                  </button>
                  
                  {/* Desktop Page Numbers */}
                  <div className="hidden sm:flex space-x-1 px-2">
                    {getPageNumbers().map((pageNum, idx) => {
                      if (pageNum === '...') {
                        return <span key={`ellipsis-${idx}`} className="px-3 py-2 text-[#8C9086] flex items-center justify-center">...</span>;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`w-9 h-9 rounded-xl text-sm font-bold flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                            pageNum === page
                              ? 'bg-[#3B82F6] text-white shadow-sm'
                              : 'text-[#8C9086] hover:bg-[#D5DABF] hover:text-[#3A3A3A]'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 text-sm font-semibold text-[#8C9086] rounded-xl hover:bg-[#D5DABF] disabled:opacity-50 disabled:hover:bg-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#8C9086]/50"
                  >
                    Next &rarr;
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* API Details Modal */}
      <ApiUsageModal
        show={showApiModal}
        onClose={closeApiModal}
        selectedUserApis={selectedUserApis}
      />
    </div>
  );
};

export default AdminAnalytics;